(ns frontend.ux-overhaul.nev.interim.old-nev-components
  (:require
   [clojure.set :as set]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.entity-utils :as eu]
   [common.localization :as loc]
   [common.schema.calculated-field :as calculated-field]
   [common.schema.constraints :as constraints]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.field :as field]
   [common.schema.registry :as registry]
   [frontend.actions.entity :as entity-actions]
   [ui.util :as uu]
   [frontend.components.field-editor :as field-editor]
   [frontend.intelligence.icon :as intelligence-icon]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.utils :as fu]
   [frontend.view.components.account :as account]
   [frontend.view.components.form.components.form-ratings :as form-ratings]
   [frontend.view.components.new-tags.view :as new-tags]
   [frontend.view.components.progress-indicator :as progress-indicator]
   [frontend.view.components.ratings :as ratings]
   [frontend.view.modals.account-select.view :as account-select]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.options-management.view :as options-management]
   [frontend.view.modals.ratings.deal :as ratings-deal]
   [potpuri.core :refer [deep-merge dissoc-in fn-> fn->> index-by]]
   [reagent.core :as r]
   [ui.components.button.view :as button]))

;;
;; entity-type based defaults:
;;

(def entity-type->view-id
  {:entity.type/company           :company
   :entity.type/fof-asset         :fof-asset
   :entity.type/fund              :fund
   :entity.type/fund-commitment   :fund-commitment
   :entity.type/fundraising-opportunity :fundraising-opportunity
   :entity.type/holding           :holding
   :entity.type/investor          :investor
   :entity.type/oppo              :deal
   :entity.type/other-asset       :other-asset
   :entity.type/person            :person
   :entity.type/real-estate       :real-estate
   :entity.type/sales-opportunity :sales-opportunity
   :entity.type/sale-agreement    :sale-agreement
   :entity.type/exit-opportunity  :exit-opportunity})

(defn default-view-id
  [{entity-type :entity/type}]
  (get entity-type->view-id entity-type (keyword (name entity-type))))

(def entity-type->editor-module-id
  {:entity.type/company           :company-information
   :entity.type/fof-asset         :fof-asset-information
   :entity.type/fund              :fund-information
   :entity.type/fund-commitment   :fund-commitment-information
   :entity.type/fundraising-opportunity :fundraising-opportunity-information
   :entity.type/holding           :holding-information
   :entity.type/investor          :investor-info-v3
   :entity.type/oppo              :oppo-information
   :entity.type/other-asset       :other-asset-information
   :entity.type/person            :person-information-information
   :entity.type/real-estate       :real-estate-information
   :entity.type/sales-opportunity :sales-opportunity-information
   :entity.type/sale-agreement    :sale-agreement-information
   :entity.type/exit-opportunity  :exit-opportunity-information})

(defn default-editor-module-id
  [{entity-type :entity/type}]
  (get entity-type->view-id
       entity-type
       (keyword (str (name entity-type)
                     "-information"))))

(def entity-type->header-fields
  {:entity.type/company           [:asset/description :company/next-development]
   :entity.type/fof-asset         [:asset/description]
   :entity.type/fund              [:fund/description]
   :entity.type/fund-commitment   [:fund-commitment/overview]
   :entity.type/fundraising-opportunity [:fundraising-opportunity/description
                                         :fundraising-opportunity/next-steps]
   :entity.type/sales-opportunity [:sales-opportunity/description :sales-opportunity/next-steps]
   :entity.type/holding           [:holding/exit-plan :holding/recent-status]
   :entity.type/investor          [:investor/description]
   :entity.type/oppo              [:oppo/case-note :oppo/status-and-next-steps]
   :entity.type/other-asset       [:asset/description]
   :entity.type/person            [:person/description :person/notes]
   :entity.type/real-estate       [:asset/description]})

(defn default-header-fields
  [entity]
  (let [entity-type (:entity/type entity)
        header-fields (get entity-type->header-fields entity-type)
        capman_re? (and (= entity-type :entity.type/oppo)
                        (tenant-customizations/capman-is-real-estate-pipeline? (:oppo/pipeline
                                                                                entity)))
        header-fields (if capman_re?
                        [:oppo/deal-description]
                        header-fields)]
    header-fields))

(def entity-type->remove-fields
  {:entity.type/company         [:asset/description :company/website]
   :entity.type/fof-asset       [:asset/description]
   :entity.type/fund            [:fund/name]
   :entity.type/fund-commitment [:fund-commitment/name :fund-commitment/investor
                                 :fund-commitment/fund]
   :entity.type/fundraising-opportunity [:fundraising-opportunity/opportunity-name
                                         :fundraising-opportunity/fund-commitment
                                         :fundraising-opportunity/credit
                                         :fundraising-opportunity/investor
                                         :common/team-members]
   :entity.type/holding         [:holding/name :holding/fund]
   :entity.type/investor        [:investor/name]
   :entity.type/oppo            [:oppo/case-note :oppo/status-and-next-steps]
   :entity.type/other-asset     [:asset/name]
   :entity.type/person          [:person/universal-email-address :person/avatar-url :person/notes
                                 :person/gender]
   :entity.type/real-estate     [:asset/name]})

;;fields hidden from entity-editor
(defn remove-editor-fields
  [entity]
  (let [entity-type (:entity/type entity)
        header-fields (default-header-fields entity)
        tenant-schema @session/tenant-schema
        tag-fields (field/entity-tag-fields tenant-schema entity-type)
        fields (concat tag-fields
                       header-fields
                       [:common/network-drive
                        :common/named-network-drives]
                       (get entity-type->remove-fields entity-type))
        investor-fields
        (when (= entity-type :entity.type/investor)
          (merge
           {(:db/id entity) (concat [:investor/name :common/network-drive :common/named-network-drives]
                                    (field/entity-tag-fields tenant-schema :entity.type/investor)
                                    header-fields)}
           (when-let [legal-entity-id (:investor/legal-entity entity)]
             (let [legal-entity @(r/track session/get-entity legal-entity-id)
                   entity-fields (->> legal-entity
                                      :entity/type
                                      (registry/get-schema tenant-schema)
                                      (map first)
                                      (set))]
               (if (= (:entity/type legal-entity) :entity.type/company)
                 {legal-entity-id (->> (set/difference entity-fields
                                                       #{:asset/street-address
                                                         :asset/street-address-2
                                                         :asset/country
                                                         :asset/city
                                                         :asset/zip-code
                                                         :company/website
                                                         :company/phone})
                                       (into []))}
                 {legal-entity-id (->> (set/difference entity-fields
                                                       #{:person/universal-email-address
                                                         :person/last-name
                                                         :person/first-name
                                                         :person/universal-phone-number
                                                         :person/location-address
                                                         :person/location-city
                                                         :person/location-country
                                                         :person/location-zip-code})
                                       (into []))})))))]
    (if (= entity-type :entity.type/investor)
      investor-fields
      fields)))

;;
;; Components:
;;

(defn add-team-member!
  [{:keys [entity-id accounts read-only?]}]
  (when-not read-only?
    (modals/open-modal!
     account-select/modal-key
     {:active-accounts accounts
      :header-text     (loc/tr :add-team-member)
      :on-select!      (fn [on-close! selected-account-id]

                         (let [entity (get @session/entities entity-id)
                               current-team (:common/team-members entity)
                               new-team (set/union current-team #{selected-account-id})]
                           ;;optimistic update:
                           (swap! session/entities assoc-in
                             [entity-id :common/team-members]
                             new-team)
                           (entity-actions/*->field-update!
                            entity-id
                            :common/team-members
                            new-team
                            (fn [_]
                              (on-close!))

                            (fn [err]
                              (let [entity (get @session/entities entity-id)
                                    team (:common/team-members entity)]
                                ;;retract optimistic update:
                                (when (= team new-team)
                                  (swap! session/entities assoc-in
                                    [entity-id :common/team-members]
                                    current-team))
                                (logging/log "Error adding team-member : " err))))))})))

(defn remove-team-member!
  [{:keys [entity-id account-id]}]
  (let [entity (get @session/entities entity-id)
        current-team (:common/team-members entity)
        new-team (set/difference current-team #{account-id})]
    ;;optimistic update:
    (swap! session/entities assoc-in [entity-id :common/team-members] new-team)
    (entity-actions/*->field-update!
     entity-id
     :common/team-members
     new-team
     (fn [_])
     (fn [err]
       (let [entity (get @session/entities entity-id)
             team (:common/team-members entity)]
         ;;retract optimistic update:
         (when (= team new-team)
           (swap! session/entities assoc-in [entity-id :common/team-members] current-team))
         (logging/log "Error removing team-member : " err))))))

(defn member-tooltip
  [entity account-id]
  (let [account @(r/track session/get-entity account-id)
        type-text (loc/tr (:entity/type entity))
        account-name (when account
                       (entity/display-string account))]
    (loc/tr :team-member-tooltip [type-text account-name])))

(defn team-members-view
  [{:keys [entity pipeline-id entity-cursor owner-attribute members-attribute]}]
  (let [members (:common/team-members @entity-cursor)
        owner (get @entity-cursor owner-attribute)
        skip (set/union members #{owner})
        ;; list only accounts that are not already team-members or the owner for this entity:
        accounts (->> (session/active-accounts pipeline-id)
                      (remove (fn [a]
                                (some #{(:db/id a)} skip))))
        accounts (sort-by (fn-> entity/display-string str string/lower-case) accounts)
        read-only? (not (f-permissions/field-write-access? members-attribute
                                                           (:entity/type entity)
                                                           pipeline-id))]

    [:div.team-members
     (doall
      (for [member members]
        ^{:key (str member)}
        [account/member-circle
         {:account-id   member
          :tooltip-text (member-tooltip @entity-cursor member)
          :on-remove!   (r/partial remove-team-member!
                                   {:entity-id  (:db/id @entity-cursor)
                                    :account-id member})}]))
     (when-not (or (empty? accounts)
                   (session/is-read-only-user?))
       [button/secondary-open
        {:tooltip (loc/tr :add-team-member)
         :on-click   (r/partial add-team-member!
                                {:entity-id  (:db/id @entity-cursor)
                                 :accounts   accounts
                                 :read-only? read-only?})}
        [:i.fal.fa-plus]])]))


(defn textarea-view
  [tenant-schema tenant entity enriched-entity attribute]
  (let [field-schema-orig (registry/get-field-schema tenant-schema attribute)
        field-schema
        (merge field-schema-orig
               {:access/type (f-permissions/access-to-field-long
                              attribute
                              (:entity/type entity)
                              (fu/pipeline-id entity))
                :editor/type (->> field-schema-orig
                                  :field/default-visibility
                                  :editor)})
        state-atom (r/atom {})
        entity-atom (r/cursor session/entities [(:db/id entity)])
        value (fu/get-value tenant tenant-schema entity attribute)
        enriched-value (get enriched-entity attribute)
        read-only?
        (not (f-permissions/field-write-access? attribute
                                                (:entity/type entity)
                                                (fu/pipeline-id entity)))]
    (if-not field-schema-orig
      (logging/error "nev.components.textarea-view : Misconfiguration - no field schema : "
                     attribute)
      [field-editor/view
       (merge
        field-schema
        {:drag-options      {:hide-drag-area? true}
         :state-atom        state-atom
         :containing-entity entity
         :value             (or value enriched-value)
         :class-name        (fu/cs (:class-name field-schema)
                                   (when (and
                                          (fu/empty-value? value)
                                          (fu/empty-value? enriched-value))
                                     "empty"))
         :label             (loc/field-name attribute field-schema)
         :read-only?        read-only?
         :disabled?         read-only?
         :placeholder       (field/placeholder field-schema)
         ;; tools are configured by need - all handlers are inside as currently the tools have no side effects
         :tools             [:tool-item/cancel :tool-item/save :tool-item/edit :tool-item/copy]
         :on-save!          (fn [attribute new-value on-success! on-error!]
                              ;;make sure local state is cleared when no changes:
                              (when (and (= new-value value)
                                         (fn? on-success!))
                                (on-success!))
                              (when-not (= new-value value)
                                ;;optimistic save to frontend db:
                                (swap! entity-atom assoc attribute new-value)

                                (entity-actions/*->field-update!
                                 (:db/id entity)
                                 attribute
                                 new-value
                                 (fn [res]
                                   (when on-success!
                                     (on-success!)))
                                 (fn [err]
                                   ;;retract the optimistic save above
                                   (when (= new-value (get @entity-atom attribute))
                                     (swap! entity-atom assoc attribute value))
                                   (on-error! err)))))})])))



(defn website-view
  [{:keys [entity url-attribute enriched-entity entity-atom]}]
  (let [tenant-schema @session/tenant-schema
        field-schema (registry/get-field-schema tenant-schema url-attribute)
        field-schema
        (merge field-schema
               {:access/type (f-permissions/access-to-field-long
                              url-attribute
                              (:entity/type entity)
                              (fu/pipeline-id entity))
                :editor/type (->> field-schema
                                  :field/default-visibility
                                  :editor)})
        state-atom (r/atom {})]
    (fn [{:keys [entity url-attribute enriched-entity entity-atom]}]
      (let [website (or (get entity url-attribute)
                        (get enriched-entity url-attribute))
            read-only?
            (not (f-permissions/field-write-access? url-attribute
                                                    (:entity/type entity)
                                                    (fu/pipeline-id entity)))]
        [:span.entity-url-attribute
         [field-editor/view
          (merge field-schema
                 {:drag-options      {:hide-drag-area? true}
                  :containing-entity entity
                  :state-atom        state-atom             ;; state is passed  from outside so debugging is easier
                  :value             website
                  :class-name        (fu/cs (:class-name field-schema)
                                            "entity-header-url-attribute"
                                            (when (empty? website) "empty" (str url-attribute)))
                  :label             (or (->> field-schema
                                              :field/translations
                                              :en)
                                         (loc/field-name url-attribute field-schema)
                                         (loc/tr url-attribute))
                  :hide-label?       true
                  :read-only?        read-only?
                  :disabled?         read-only?
                  :placeholder       (field/placeholder field-schema)
                  ;; tools are configured by need - all handlers are inside as currently the tools have no side effects
                  :tools             [:tool-item/cancel :tool-item/save :tool-item/edit
                                      :tool-item/copy :tool-item/open-url]
                  :on-save!          (fn [attribute new-website on-success! on-error!]
                                       ;;make sure local state is cleared when no changes:
                                       (when (and (= new-website website)
                                                  (fn? on-success!))
                                         (on-success!))
                                       (when-not (= new-website website)
                                         ;;optimistic save to frontend db:
                                         (swap! entity-atom assoc attribute new-website)
                                         (entity-actions/*->field-update!
                                          (:db/id entity)
                                          attribute
                                          new-website
                                          (fn [res]
                                            (when on-success!
                                              (on-success!)))
                                          (fn [err]
                                            ;;retract the optimistic save above
                                            (when (= new-website (get @entity-atom attribute))
                                              (swap! entity-atom assoc attribute website))
                                            (on-error! err)))))})]]))))


(defn email-view
  [{:keys [entity email-attribute enriched-entity entity-atom]}]
  (let [tenant-schema @session/tenant-schema
        field-schema (registry/get-field-schema tenant-schema email-attribute)
        field-schema
        (merge field-schema
               {:access/type (f-permissions/access-to-field-long
                              email-attribute
                              (:entity/type entity)
                              (fu/pipeline-id entity))
                :editor/type (->> field-schema
                                  :field/default-visibility
                                  :editor)})
        state-atom (r/atom {})]
    (fn [{:keys [entity email-attribute enriched-entity entity-atom read-only?]}]
      (let [email (or (get entity email-attribute)
                      (get enriched-entity email-attribute))
            read-only?
            (or read-only?
                (not (f-permissions/field-write-access? email-attribute
                                                        (:entity/type entity)
                                                        (fu/pipeline-id entity))))]
        [:span.entity-url-attribute
         [field-editor/view
          (merge
           field-schema
           {:drag-options      {:hide-drag-area? true}
            :containing-entity entity
            :state-atom        state-atom                   ;; state is passed  from outside so debugging is easier
            :value             email
            :class-name        (fu/cs (:class-name field-schema)
                                      "entity-header-url-attribute"
                                      (when (empty? email)
                                        "empty")
                                      (str email-attribute))
            :label             (or (->> field-schema
                                        :field/translations
                                        :en)
                                   (loc/field-name email-attribute field-schema)
                                   (loc/tr email-attribute))
            :hide-label?       true
            :read-only?        read-only?
            :disabled?         read-only?
            :placeholder       (field/placeholder field-schema)
            ;; tools are configured by need - all handlers are inside as currently the tools have no side effects
            :tools             [:tool-item/cancel :tool-item/save :tool-item/edit :tool-item/copy]
            :on-save!          (fn [attribute new-email on-success! on-error!]
                                 ;;make sure local state is cleared when no changes:
                                 (when (and (= new-email email)
                                            (fn? on-success!))
                                   (on-success!))
                                 (when-not (= new-email email)
                                   ;;optimistic save to frontend db:
                                   (swap! entity-atom assoc attribute new-email)
                                   (entity-actions/*->field-update!
                                    (:db/id entity)
                                    attribute
                                    new-email
                                    (fn [res]
                                      (when on-success!
                                        (on-success!)))
                                    (fn [err]
                                      ;;retract the optimistic save above
                                      (when (= new-email (get @entity-atom attribute))
                                        (swap! entity-atom assoc attribute email))
                                      (on-error! err)))))})]]))))


(defn name-view
  [{:keys [entity read-only?]}]
  (let [entity-id (:db/id entity)
        tenant-schema @session/tenant-schema
        raw-input (r/atom nil)
        has-focus (r/atom false)
        edit-atom (r/atom false)
        error-atom (r/atom nil)
        focus-state (atom {})
        entity-atom (r/cursor session/entities [entity-id])
        name-attribute (registry/resolve-name-attribute (:entity/type entity))
        name-schema (registry/get-field-schema tenant-schema name-attribute)
        read-only? (or read-only?
                       (:field/composite-attributes name-schema)
                       (not (f-permissions/field-write-access?
                             name-attribute
                             (:entity/type entity)
                             (fu/pipeline-id entity))))]
    (fn [entity]
      (let [original-name
            (entity/resolve-entity-name @entity-atom session/get-entity session/reverse-lookup)
            on-name-change! (fn [new-name]
                              ;;optimistic save
                              (swap! entity-atom assoc name-attribute new-name)
                              (entity-actions/*->field-update!
                               entity-id
                               name-attribute
                               new-name
                               (fn [res]
                                 #_(when on-success!
                                     (on-success!)))
                               (fn [err]
                                 ;;retract the optimistic save above
                                 (js/console.log "ERROR saving name " err)
                                 (when (= new-name (get @entity-atom name-attribute))
                                   (swap! entity-atom assoc name-attribute original-name))
                                 (reset! error-atom "Duplicate value")
                                 (reset! edit-atom true))))
            ;;(on-error! err)
            ;;TODO show error!

            on-cancel! (fn [_]
                         (reset! raw-input nil))
            on-key-down! (fn [e]
                           (let [key (.-keyCode e)]
                             (when (= key 13)
                               (-> e
                                   .-target
                                   .blur))    ;; Enter = blur -> save
                             (when (= key 27) ;; Esc = cancel
                               (on-cancel! e)
                               (-> e
                                   .-target
                                   .blur))))]
        [:span.nev-heading-entity-name-container
         [:span.nev-heading-entity-name-text-container

          (when (or @edit-atom @error-atom)
            [:div
             [:input
              {:class-name  (fu/cs :nev-heading-entity-name-input)
               :value       (or (if (nil? @raw-input) original-name @raw-input) "")
               :ref         (fn [dom-ref]
                              (when (and dom-ref
                                         (not= dom-ref (:dom-ref @focus-state)))
                                (swap! focus-state assoc :dom-ref dom-ref)
                                (.focus dom-ref)))
               :on-change   (fn [e]
                              (let [input (-> e
                                              .-target
                                              .-value)]
                                (reset! raw-input input)))
               :disabled    read-only?
               :on-focus    #(reset! has-focus true)
               :on-blur     #(do (reset! has-focus false)
                                 (reset! edit-atom false)
                                 (reset! error-atom nil)
                                 (when (and @raw-input
                                            (not= @raw-input original-name))
                                   (on-name-change! @raw-input)))
               :on-key-down on-key-down!}]
             (when-let [error @error-atom]
               [:span.form-error
                error])])

          (when-not @edit-atom
            [:label.nev-heading-entity-name-label
             (or (if (nil? @raw-input) original-name @raw-input) "")])

          (when-not read-only?
            [:span
             {:class-name (uu/cm :nev-heading-entity-name-tools
                                 :show-on-hover
                                 (when @edit-atom
                                   :force-visible))}
             (if-not @edit-atom
               [:i.fal.fa-edit
                {:on-click #(reset! edit-atom true)}]
               [:i.fal.fa-undo
                {:on-mouse-down on-cancel!}])])]

         (when (= :holding.exit-status/exited (:holding/exit-status entity))
           [:span
            {:class-name "holdings-exited"}
            [:i.fal.fa-sign-out] (str " " (loc/tr :holding.exit-status/exited))])]))))


(defn ratings-view
  [{:keys [entity entity-cursor pipeline-id ratings-attribute tooltip-text entity-id]}]
  (let [read-only? (not (f-permissions/field-write-access? ratings-attribute
                                                           (:entity/type entity)
                                                           pipeline-id))]
   (let [ratings (get @entity-cursor ratings-attribute)]
     [button/secondary-open
      {:tooltip tooltip-text}
      {:class-name (fu/cs (if read-only?
                            :ratings-disabled
                            :ratings-block)
                          (if (empty? ratings)
                            :ratings-empty
                            :ratings-non-empty))
       :on-click
       (fn [_]
         (when-not read-only?
           (modals/open-modal!
            ratings-deal/modal-key
            {:label         tooltip-text
             :accounts      (session/active-accounts-with-pipeline-access pipeline-id)
             :value         ratings
             :step          form-ratings/step
             :scale         form-ratings/scale
             :show-buttons? true
             :on-change!    (fn [_])
                  ;; do not send live updates - wait for submit button

             :on-submit!    (fn [ratings-after]
                              (when-not (= ratings-after ratings)
                                     ;;optimistic save to frontend db:
                                (swap! entity-cursor assoc ratings-attribute ratings-after)
                                (entity-actions/*->field-update!
                                 entity-id
                                 ratings-attribute
                                 ratings-after
                                 (fn [res])
                                 (fn [err]
                                        ;;retract the optimistic save above
                                   (when (= ratings-after (get @entity-cursor ratings-attribute))
                                     (swap! entity-cursor assoc ratings-attribute ratings)
                                     (deref entity-cursor))
                                   (logging/log "Show ratings update failed!")))
                                (deref entity-cursor)))})))}
      [:span.ratings-text
       (when-not (empty? ratings)
         (loc/tr :ratings-value))]
      [:span
       {:class-name (fu/cs :rounded-item__content
                           (when (empty? ratings) :no-value))}
       (str (ratings/round-average (if (not-empty ratings)
                                     (/ (reduce + (map #(* ratings/scale-count %) (vals ratings)))
                                        (count ratings))
                                     (loc/tr :ratings-no-value-in-deal))))]])))

(defn select-owner!
  [{:keys [entity-cursor entity-id owner-attribute pipeline-id]}]
  (let [accounts (->> (session/active-accounts pipeline-id)
                      (remove (fn [a]
                                (= (:db/id a) (get @entity-cursor owner-attribute)))))]
    (when-not (empty? accounts)
      (modals/open-modal!
       account-select/modal-key
       {:active-accounts accounts
        :header-text     (loc/tr :select-owner)
        :on-select!      (fn [on-close! selected-account-id]
                           (let [entity (get @session/entities entity-id)
                                 current-owner (get entity owner-attribute)]
                             ;;optimistic update:
                             (swap! entity-cursor assoc owner-attribute selected-account-id)
                             (entity-actions/*->field-update!
                              entity-id
                              owner-attribute
                              selected-account-id
                              (fn [_]
                                (on-close!))

                              (fn [err]
                                ;;retract optimistic update:
                                (when (= (get @entity-cursor owner-attribute) selected-account-id)
                                  (swap! entity-cursor assoc owner-attribute current-owner))
                                (logging/log "Error updating owner : " err)
                                (deref entity-cursor)))

                             (deref entity-cursor)))}))))

(defn display
  [entity-id]
  (let [entity @(r/track session/get-entity entity-id)]
    (when entity
      (entity/display-string entity))))

(defn owner-stage-rating-module
  [{:keys [entity-cursor view-state entity
           entity-stage-attribute stage-entity-type stage-pipeline-id stage-pipeline-attribute
           ratings-attribute owner-attribute members-attribute]}]
  (let [current-stage-id (get entity entity-stage-attribute)
        sorted-stages (->> @(r/track session/get-entities stage-entity-type)
                           (filter (fn [s]
                                     (= stage-pipeline-id (get s stage-pipeline-attribute))))
                           (sort-by :common/ord))]
    [:div
     {:class-name (fu/cs :flex-row-3-first-priority)}
     [:div.d-flex-nowrap.align-items-center
      (when (f-permissions/field-read-access? owner-attribute
                                              (:entity/type entity)
                                              stage-pipeline-id)
        [account/account-circle (get entity owner-attribute)
         (if (session/is-read-only-user?)
           identity
           (r/partial select-owner!
                      {:entity-cursor   entity-cursor
                       :entity-id       (:db/id entity)
                       :owner-attribute owner-attribute
                       :pipeline-id     stage-pipeline-id}))
         (str (loc/tr :common/owner)
              ": "
              (let [owner-id (get @entity-cursor owner-attribute)
                    owner-name @(r/track display owner-id)]
                owner-name))])

      (when (f-permissions/field-read-access? members-attribute
                                              (:entity/type entity)
                                              stage-pipeline-id)
        [team-members-view
         {:entity            entity
          :pipeline-id       stage-pipeline-id
          :entity-cursor     entity-cursor
          :owner-attribute   owner-attribute
          :members-attribute members-attribute}])]

     #_(when (seq sorted-stages)
         [:span
          [progress-indicator/view
           {:stages
            (for [stage sorted-stages]
              (let [stage-name (entity/display-string stage)]
                {:kw           (:db/id stage)
                 :tooltip-text (cond (= (:db/id stage) current-stage-id)
                                     stage-name
                                     (session/is-read-only-user?)
                                     stage-name
                                     :else
                                     (loc/tr :move-to-stage [stage-name]))
                 :on-click!    (when-not (session/is-read-only-user?)
                                 (fn [_]
                                   (let [new-stage-id (:db/id stage)]
                                     (when-not (= new-stage-id current-stage-id)
                                       ;;optimistic save to frontend db:
                                       (swap! entity-cursor assoc entity-stage-attribute new-stage-id)

                                       (entity-actions/*->field-update!
                                        (:db/id entity)
                                        entity-stage-attribute
                                        new-stage-id
                                        (fn [res])
                                        (fn [err]
                                          ;;retract the optimistic save above
                                          (when (= new-stage-id
                                                   (get @entity-cursor entity-stage-attribute))
                                            (swap! entity-cursor assoc
                                              entity-stage-attribute
                                              current-stage-id)
                                            (deref entity-cursor))
                                          (logging/log "Set stage update failed!")))
                                       ;; deref cursor to sync with atom
                                       (= (:db/id entity) (:db/id @entity-cursor))))))}))
            :current-stage current-stage-id}]])
     (when (and ratings-attribute
                (not (session/is-guest?))
                (f-permissions/field-read-access? ratings-attribute
                                                  (:entity/type entity)
                                                  stage-pipeline-id))
       [:span.flex.ratings__container
        [ratings-view
         {:entity            entity
          :entity-cursor     entity-cursor
          :pipeline-id       stage-pipeline-id
          :ratings-attribute ratings-attribute
          :entity-id         (:db/id entity)
          :tooltip-text      (if (not-empty (get entity ratings-attribute))
                               (loc/tr :ratings-tooltip-modify [(entity/display-string entity)])
                               (loc/tr :ratings-tooltip-add-value
                                   [(entity/display-string entity)]))}]])]))


(defn field-editor
  [{:keys [tenant attribute state-atom entity-template-lookup entity enriched-entity tools]}]
  (let [tenant-schema @session/tenant-schema
        entity-type (:entity/type entity)
        field-schema (get entity-template-lookup attribute)
        resolve-value (fn []
                        (let [entity @state-atom]
                          (fu/get-value tenant tenant-schema entity attribute)))
        pipeline-attribute (pipeline/pipeline-key entity-type)
        pipeline-id (or (get @state-atom pipeline-attribute)
                        (get entity pipeline-attribute))
        pipeline-url (pipeline/pipeline-url entity-type)
        read-only? (or (session/is-read-only-user?)
                       (not
                        (f-permissions/field-write-access? attribute entity-type pipeline-id)))
        value (resolve-value)
        intelligence-value (get enriched-entity attribute)
        no-value? (and (nil? value)
                       (fu/empty-value? intelligence-value))
        is-intelligence-value? (and (nil? value)
                                    (contains? enriched-entity attribute))]
    [field-editor/view
     (merge
      field-schema
      {:attribute          attribute
       :state-atom         state-atom                       ;; state is passed  from outside so debugging is easier
       :value              (if is-intelligence-value? intelligence-value value)
       :containing-entity  entity
       :class-name         (fu/cs (:class-name field-schema)
                                  (when no-value? "empty"))
       :entity-types-atom  session/entity-types
       :entity-type        entity-type
       :entity-unique-name (entity/unique-name-or-id entity)
       ;; TODO: take language from user / tenant config -> default to :en
       ;; if only :en in field/translations use :en
       :label              (or (->> field-schema
                                    :field/translations
                                    :en)
                               (loc/field-name attribute field-schema)
                               (loc/tr attribute))              ;; we select label outside of field-editor so we can customize label if needed
       :read-only?         read-only?

       ;:field-access [:x :w :r] ;; TODO: Fairly urgent - change the way field editors access is resolved everywhere so that the access is passed from outside
       :open-settings!     (fn []
                             (modals/open-modal! options-management/modal-key
                                                 {:pipeline-attribute pipeline-attribute
                                                  :selected-field     attribute}))
       :disabled?          read-only?
       :placeholder        (field/placeholder field-schema)
       ;; tools are configured by need - all handlers are inside as currently the tools have no side effects
       :tools              (or tools
                               (concat [:tool-item/cancel :tool-item/save :tool-item/edit
                                        :tool-item/copy :tool-item/open-url :tool-item/clear
                                        :tool-item/debug]
                                       (when (and (session/is-admin?)
                                                  (->> field-schema
                                                       :named-value-attribute))
                                         [:tool-item/settings])))
       :validate!          (fn [attribute value]
                             (let [schema (get entity-template-lookup attribute)
                                   field-constraints (:field/constraints schema)
                                   errors (constraints/validate field-constraints
                                                                (:db/id entity)
                                                                value
                                                                entity
                                                                (partial session/unique-check
                                                                         false
                                                                         (partial
                                                                          routing/open-entity))
                                                                loc/tr
                                                                logging/warn)]
                               errors))
       :error-text         nil                              ;; TODO: use editor-atom to store validation and save errors
       :on-save!           (fn [attribute new-value on-success! on-error!]
                             (let [value (resolve-value)]
                               ;;make sure local state is cleared when no changes:
                               (if (and (= new-value value)
                                        (fn? on-success!))
                                 (do
                                   (on-success!))
                                 (when-not (= new-value value)
                                   (if-let [create-value (:new-option new-value)]
                                     (entity-actions/*->field-update-with-new-entity!
                                      (:db/id entity)
                                      attribute
                                      value
                                      create-value
                                      (fn [res]
                                        (when on-success! (on-success!)))
                                      (fn [err]
                                        (when on-error! (on-error! err))))
                                     (do
                                       ;;optimistic save to frontend db:
                                       (swap! state-atom assoc attribute new-value)
                                       (deref state-atom)
                                       ;; Index at once. A listing that shows this entity
                                       ;; reads OpenSearch, and the completion tells it to refresh.
                                       (entity-actions/*->field-update-instant-indexing!
                                        (:db/id entity)
                                        attribute
                                        new-value
                                        (fn [res]
                                          (when on-success!
                                            (on-success!)))
                                        (fn [err]
                                          ;;retract the optimistic save above
                                          (when (= new-value (get @state-atom attribute))
                                            (swap! state-atom assoc attribute value)
                                            (deref state-atom))
                                          (on-error! err)))))))))}

      (when is-intelligence-value?                          ;; we decide source outside
        {:source-options {:source-icon-fn intelligence-icon/view}})
      (when-let [secondary-attribute (:field/secondary-attribute field-schema)] ;; secondary attribute
        {:secondary-view-args
         (let [sec-templ (get entity-template-lookup secondary-attribute)
               secondary-field-schema (registry/get-field-schema tenant-schema secondary-attribute)]
           (merge
            sec-templ
            {:attribute  secondary-attribute
             :state-atom state-atom
             ;; TODO: fix composite-attribute to check calculated field instead
             :value      (if-let [composite-attributes (:field/composite-attributes
                                                        secondary-field-schema)]
                           (when-let [calc-function (:field/calculated-field
                                                     secondary-field-schema)]
                             (calculated-field/calculate
                              {:calc-fn          calc-function
                               :entity-data      entity
                               :entity-lookup    session/get-entity
                               :reverse-lookup   session/reverse-lookup
                               :tr               loc/tr
                               :bigdec->float    fu/bigdec->float
                               :resolve-value    (partial eu/get-value tenant tenant-schema)
                               :float->bigdec    fu/float->bigdec
                               :get-field-schema (partial registry/get-field-schema tenant-schema)
                               :display-string   entity/display-string}))
                           (or (get @state-atom secondary-attribute)
                               (when (= (:field/data-type sec-templ) :data.type/currency-symbol)
                                 (:tenant/default-currency @session/tenant))))
             :label      (loc/tr secondary-attribute)           ;; when we have custom dynamic fields the labels come from tenant configuration
             :error-text nil}))}))]))


(defn header-module
  [{:keys [component-state
           view-state
           entity
           entity-atom
           enriched-entity
           entity-template-lookup
           selected-fields
           field-rows
           url-attribute
           email-attribute
           read-only-email?
           related-tags-entity-id
           pipeline-id
           logo-url]}]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema]
    [:div.entity-modules
     [:div.entity-module
      [:div.entity-module-content-container
       [:div.entity-module-content
        [:div.row
         [:div.entity-new-tags.d-flex.old-nev-components-override
          [new-tags/view
           {:entity-id        (:db/id entity)
            :entity-type      (:entity/type entity)
            :edit-entity-id   (:db/id entity)
            :edit-entity-type (:entity/type entity)
            :pipeline-id      pipeline-id
            :icon             :fa-tags
            :title            (loc/tr :-tags [(loc/tr (:entity/type entity))])}]
          (when-let [related-tags-entity @(r/track session/get-entity related-tags-entity-id)]
            [new-tags/view
             {:entity-id        (:db/id related-tags-entity)
              :entity-type      (:entity/type related-tags-entity)
              :edit-entity-id   (:db/id related-tags-entity)
              :pipeline-id      pipeline-id
              :edit-entity-type (:entity/type related-tags-entity)
              :title            (loc/tr :-tags [(loc/tr (:entity/type related-tags-entity))])}])]]
        (->>
         selected-fields
         (filter (fn [attribute]
                   (let [visible? (and attribute
                                       (some #{attribute} (:tenant/fields-in-use @session/tenant))
                                       (f-permissions/field-read-access?
                                        attribute
                                        (:entity/type entity)
                                        (fu/pipeline-id entity))
                                       (let [fs (registry/get-field-schema tenant-schema attribute)]
                                         (some #{(:field/data-type fs)}
                                               [:data.type/text :data.type/text-multiline
                                                :data.type/text-richtext])))]
                     visible?)))
         (partition 2 2 [nil])
         (map-indexed
          (fn [partition-idx chunk-attributes]
            (let [non-padded-attributes (remove nil? chunk-attributes)]
              (case (count non-padded-attributes)
                2
                [:div
                 {:key (str "entity-nev-" (:db/id entity) "-header-" partition-idx)
                  :class-name "grid md:grid-cols-2 gap-4 pt-2"}
                 [:div
                  [textarea-view tenant-schema tenant entity enriched-entity
                   (first non-padded-attributes)]]
                 [:div
                  [textarea-view tenant-schema tenant entity enriched-entity
                   (second non-padded-attributes)]]]
                1
                [:div
                 {:key (str "entity-nev-" (:db/id entity) "-header-" partition-idx)
                  :class-name "grid md:grid-cols-1 gap-4 pt-2"}
                 [:div
                  [textarea-view tenant-schema tenant entity enriched-entity
                   (first non-padded-attributes)]]]
                nil))))
         (remove nil?)
         (doall))
        (->>
         field-rows
         (map-indexed
          (fn [row-idx row]
            (let [attributes (->> row
                                  (filter (fn [attribute]
                                            (and attribute
                                                 (f-permissions/field-read-access?
                                                  attribute
                                                  (:entity/type entity)
                                                  (fu/pipeline-id entity))))))]
              (case (count attributes)
                2 [:div
                   {:key (str "entity-nev-" (:db/id entity) "-header-row-" row-idx)
                    :class-name "grid md:grid-cols-2 gap-4"}
                   [:div
                    [field-editor
                     {:attribute       (first attributes)
                      :tenant          tenant
                      :entity          entity
                      :enriched-entity enriched-entity
                      :state-atom      entity-atom
                      :entity-template-lookup entity-template-lookup}]]

                   [:div
                    [field-editor
                     {:attribute       (second attributes)
                      :tenant          tenant
                      :entity          entity
                      :enriched-entity enriched-entity
                      :state-atom      entity-atom
                      :entity-template-lookup entity-template-lookup}]]]

                1 [:div
                   {:key (str "entity-nev-" (:db/id entity) "-header-row-" row-idx)
                    :class-name "grid md:grid-cols-1 gap-4"}
                   [:div
                    [field-editor
                     {:attribute       (first attributes)
                      :tenant          tenant
                      :entity          entity
                      :enriched-entity enriched-entity
                      :state-atom      entity-atom
                      :entity-template-lookup entity-template-lookup}]]]

                nil))))
         (remove empty?)
         (doall))]]]]))
